import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setHasNewRental, setHasNewDeposit } from 'src/reduxStore/slices/generalSlice'
import {
  useGetRentalAllBookingQuery,
  useGetAllUploadedPaymentsQuery,
} from 'src/reduxStore/reduxToolkitQuery'
import tone from 'src/audio/notify.mp3'

/**
 * GlobalNotificationWatcher
 *
 * This invisible component polls both rental bookings and deposit data
 * from any page. When new records are detected, it:
 * 1. Dispatches Redux actions (triggers the red dot + popup)
 * 2. Plays the notification tone
 *
 * This ensures notifications work even when the user is on a different tab.
 */
const GlobalNotificationWatcher = () => {
  const dispatch = useDispatch()
  const poolingInterval = useSelector((state) => state.general.poolingInterval)
  const [userInteracted, setUserInteracted] = useState(false)

  // Refs for tracking previous data state
  const rentalPrevDate = useRef(null)
  const depositPrevDate = useRef(null)
  const rentalInitialized = useRef(false)
  const depositInitialized = useRef(false)

  // Check for new rental bookings every 5 seconds.
  const { data: rentalData, isLoading: rentalLoading, isFetching: rentalFetching } =
    useGetRentalAllBookingQuery({}, { pollingInterval: 5000 })

  // Poll deposit data
  const { data: depositData, isLoading: depositLoading, isFetching: depositFetching } =
    useGetAllUploadedPaymentsQuery(
      { limit: 9999, skip: 0 },
      { pollingInterval: poolingInterval }
    )

  // Detect user interaction to allow audio playback
  useEffect(() => {
    const enableSound = () => setUserInteracted(true)
    window.addEventListener('click', enableSound)
    window.addEventListener('keydown', enableSound)
    window.addEventListener('touchstart', enableSound)
    return () => {
      window.removeEventListener('click', enableSound)
      window.removeEventListener('keydown', enableSound)
      window.removeEventListener('touchstart', enableSound)
    }
  }, [])

  const playTone = async () => {
    if (!userInteracted) return
    try {
      const audio = new Audio(tone)
      audio.currentTime = 0
      await audio.play()
    } catch (err) {
      console.warn('GlobalNotificationWatcher: Audio playback failed', err)
    }
  }

  const getLatestDate = (data, dateFields) => {
    if (!data || !Array.isArray(data) || data.length === 0) return null
    const dates = data
      .map((record) => {
        for (const field of dateFields) {
          if (record[field]) return record[field]
        }
        return null
      })
      .filter(Boolean)
      .map((d) => new Date(d))
    return dates.length > 0 ? new Date(Math.max(...dates)) : null
  }

  // Watch rental data for new records
  useEffect(() => {
    if (rentalLoading || rentalFetching) return
    const bookings = rentalData?.bookings
    if (!bookings || bookings.length === 0) return

    const latestDate = getLatestDate(bookings, [
      'createdAt',
      'bookingDate',
      'date',
      'timestamp',
      'startDate',
      'updatedAt',
    ])
    if (!latestDate) return

    if (!rentalInitialized.current) {
      // First load — store baseline, don't notify
      rentalPrevDate.current = latestDate
      rentalInitialized.current = true
      return
    }

    if (rentalPrevDate.current && latestDate > rentalPrevDate.current) {
      console.log('🔔 GlobalWatcher: New rental booking detected!')
      dispatch(setHasNewRental(true))
      playTone()
    }

    rentalPrevDate.current = latestDate
  }, [rentalData, rentalLoading, rentalFetching])

  // Watch deposit data for new records
  useEffect(() => {
    if (depositLoading || depositFetching) return
    const payments = Array.isArray(depositData) ? depositData : null
    if (!payments || payments.length === 0) return

    const latestDate = getLatestDate(payments, ['createdAt', 'date', 'timestamp'])
    if (!latestDate) return

    if (!depositInitialized.current) {
      depositPrevDate.current = latestDate
      depositInitialized.current = true
      return
    }

    if (depositPrevDate.current && latestDate > depositPrevDate.current) {
      console.log('🔔 GlobalWatcher: New deposit detected!')
      dispatch(setHasNewDeposit(true))
      playTone()
    }

    depositPrevDate.current = latestDate
  }, [depositData, depositLoading, depositFetching])

  // This component renders nothing — it's purely a data watcher
  return null
}

export default GlobalNotificationWatcher
